import { randomNumber } from '@arcadiapower/warbler';
import { FetchUtilityResponse } from '~/services/fetch-utility';
import { FunnelTrack } from '~/typings/graphql';
import { STATE_IDS, UTILITY_IDS } from '~/utils/constants';

/**
 *  Generates a random utility account number for testing purposes.
 */
export const randomAccountNumber = (): string => {
  return randomNumber(10).toString();
};

const vnmElegibleUtilityProducts = [
  {
    id: 3,
    productType: 'BillPayService',
  },
  {
    id: 178,
    productType: 'Wind',
  },
  {
    id: 177,
    productType: 'Wind',
  },
  {
    id: 8,
    productType: 'VNM',
  },
  {
    id: 312,
    productType: 'Funding',
  },
  {
    id: 212,
    productType: 'Membership',
  },
];

export const TEST_STATES = {
  ca: { abbreviation: 'CA', id: STATE_IDS.CA, name: 'California' },
  co: { abbreviation: 'CO', id: STATE_IDS.CO, name: 'Colorado' },
  dc: { abbreviation: 'DC', id: STATE_IDS.DC, name: 'DC' },
  il: { abbreviation: 'IL', id: STATE_IDS.IL, name: 'Illinois' },
  ma: { abbreviation: 'MA', id: STATE_IDS.MA, name: 'Massachusetts' },
  md: { abbreviation: 'MD', id: STATE_IDS.MD, name: 'Maryland' },
  me: { abbreviation: 'ME', id: STATE_IDS.ME, name: 'Maine' },
  nj: { abbreviation: 'NJ', id: STATE_IDS.NJ, name: 'New Jersey' },
  nm: { abbreviation: 'NM', id: STATE_IDS.NJ, name: 'New Mexico' },
  mn: { abbreviation: 'MN', id: STATE_IDS.MN, name: 'Minnesota' },
  ny: { abbreviation: 'NY', id: STATE_IDS.NY, name: 'New York' },
  or: { abbreviation: 'OR', id: STATE_IDS.OR, name: 'Oregon' },
  ri: { abbreviation: 'RI', id: STATE_IDS.RI, name: 'Rhode Island' },
  va: { abbreviation: 'VA', id: STATE_IDS.VA, name: 'Virginia' },
  oh: { abbreviation: 'OH', id: STATE_IDS.OH, name: 'Ohio' },
  de: { abbreviation: 'DE', id: STATE_IDS.DE, name: 'Delaware' },
};

export const TEST_UTILITIES = {
  alameda: {
    id: UTILITY_IDS['Alameda Municipal Power'],
    displayName: 'Alameda Municipal Power',
    credentialType: 'UsernamePassword',
    emailIsUsername: false,
    funnelTrack: 'membership' as FunnelTrack,
    state: TEST_STATES.ca,
    zipCode: '94501',
    products: [
      {
        id: 177,
        productType: 'Wind',
      },
      {
        id: 312,
        productType: 'Funding',
      },
      {
        id: 212,
        productType: 'Membership',
      },
    ],
  },
  ameren: {
    id: UTILITY_IDS['Ameren IL'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Ameren',
    emailIsUsername: false,
    credentialType: 'APIToken',
    state: TEST_STATES.il,
    zipCode: '60479',
    products: vnmElegibleUtilityProducts,
  },
  amerenMo: {
    id: UTILITY_IDS['Ameren MO'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Ameren',
    emailIsUsername: false,
    credentialType: 'APIToken',
    state: TEST_STATES.il,
    zipCode: '63005',
    products: vnmElegibleUtilityProducts,
  },
  centralHudson: {
    id: UTILITY_IDS['Central Hudson Gas & Electric'],
    displayName: 'Central Hudson',
    emailIsUsername: true,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ny,
    zipCode: '10516',
    products: vnmElegibleUtilityProducts,
  },
  cmp: {
    id: UTILITY_IDS['Central Maine Power'],
    displayName: 'Central Maine Power',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ma,
    zipCode: '04330',
    products: vnmElegibleUtilityProducts,
  },
  conEd: {
    id: UTILITY_IDS['Consolidated Edison NY'],
    displayName: 'ConEd',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'PortalIdentifier',
    state: TEST_STATES.ny,
    zipCode: '10001',
    products: vnmElegibleUtilityProducts,
  },
  comEd: {
    id: UTILITY_IDS['Commonwealth Edison'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'ComEd',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.il,
    zipCode: '60002',
    products: vnmElegibleUtilityProducts,
  },
  ohioEdison: {
    id: UTILITY_IDS['Ohio Edison'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Ohio Edison',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.oh,
    zipCode: '43010',
    products: vnmElegibleUtilityProducts,
  },
  aepOh: {
    id: UTILITY_IDS['AEP OH'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'AEP OH',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.oh,
    zipCode: '43001',
    products: vnmElegibleUtilityProducts,
  },
  dominion: {
    id: UTILITY_IDS['Dominion Energy VA'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Dominion',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.va,
    zipCode: '20105',
    products: vnmElegibleUtilityProducts,
  },
  eversource: {
    id: UTILITY_IDS['Eversource / NSTAR'],
    displayName: 'Eversource NSTAR',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ma,
    zipCode: '02446',
    products: vnmElegibleUtilityProducts,
  },
  jcpl: {
    id: UTILITY_IDS['Jersey Central Power & Light'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'JCP&L',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.nj,
    zipCode: '07005',
    products: vnmElegibleUtilityProducts,
  },
  natGrid: {
    id: UTILITY_IDS['National Grid NY'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'National Grid',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ny,
    zipCode: '13327',
    products: vnmElegibleUtilityProducts,
  },
  natGridMa: {
    id: UTILITY_IDS['National Grid MA'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'National Grid',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ma,
    zipCode: '01005',
    products: vnmElegibleUtilityProducts,
  },
  unitilMa: {
    id: UTILITY_IDS['Unitil MA'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Unitil MA',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ma,
    zipCode: '01005',
    products: vnmElegibleUtilityProducts,
  },
  nySeg: {
    id: UTILITY_IDS.NYSEG,
    displayName: 'NYSEG',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ny,
    zipCode: '12421',
    products: vnmElegibleUtilityProducts,
  },
  orange: {
    id: UTILITY_IDS['Orange and Rockland'],
    displayName: 'Orange and Rockland',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ny,
    zipCode: '10901',
    products: [
      {
        id: 177,
        productType: 'Wind',
      },
      {
        id: 312,
        productType: 'Funding',
      },
      {
        id: 212,
        productType: 'Membership',
      },
    ],
  },
  pepco: {
    id: UTILITY_IDS['PEPCO MD'],
    displayName: 'Pepco',
    emailIsUsername: false,
    funnelTrack: 'solar' as FunnelTrack,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.md,
    zipCode: '20814',
    products: vnmElegibleUtilityProducts,
  },
  pnm: {
    id: UTILITY_IDS['Public Service Company of NM'],
    displayName: 'Public Service Company of NM',
    emailIsUsername: false,
    funnelTrack: FunnelTrack.Waitlist,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.nm,
    zipCode: '87001',
    products: vnmElegibleUtilityProducts,
  },
  portlandGE: {
    id: UTILITY_IDS['Portland General Electric'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Portland General Electric',
    emailIsUsername: true,
    credentialType: 'UsernamePassword',
    state: { abbreviation: 'OR', name: 'Oregon', id: 38 },
    zipCode: '97002',
    products: vnmElegibleUtilityProducts,
  },
  psegNj: {
    id: UTILITY_IDS['PSE&G NJ'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'PSE&G NJ',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.nj,
    zipCode: '07001',
    products: vnmElegibleUtilityProducts,
  },
  sdge: {
    id: UTILITY_IDS['SDG&E'],
    funnelTrack: 'membership' as FunnelTrack,
    displayName: 'SDG&E',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.ca,
    zipCode: '92116',
    products: vnmElegibleUtilityProducts,
  },
  xcelNM: {
    id: UTILITY_IDS['Xcel Energy NM'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Xcel Energy NM',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.nm,
    zipCode: '88101',
    products: vnmElegibleUtilityProducts,
  },
  xcelMN: {
    id: UTILITY_IDS['Xcel Energy MN'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Xcel Energy MN',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.mn,
    zipCode: '88101',
    products: vnmElegibleUtilityProducts,
  },
  xcelCO: {
    id: UTILITY_IDS['Xcel Energy CO'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Xcel Energy CO',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.co,
    zipCode: '88101',
    products: vnmElegibleUtilityProducts,
  },
  delmarvaDE: {
    id: UTILITY_IDS['Delmarva Power DE'],
    funnelTrack: 'solar' as FunnelTrack,
    displayName: 'Delmarva Power DE',
    emailIsUsername: false,
    credentialType: 'UsernamePassword',
    state: TEST_STATES.de,
    zipCode: '19701',
    products: vnmElegibleUtilityProducts,
  },
};

export const fetchUtilityResponse = (name: keyof typeof TEST_UTILITIES): FetchUtilityResponse => ({
  id: TEST_UTILITIES[name].id,
  displayName: TEST_UTILITIES[name].displayName,
  funnelTrack: TEST_UTILITIES[name].funnelTrack,
  state: TEST_UTILITIES[name].state,
});
